/**
 * Sizing helpers for trade-tape snapshots sent to the AI agent — shared by
 * the tape-snapshot mirror (`tape-snapshot-store.ts`) and the `/analyze`
 * hidden-context builder (`build-trade-prompt.ts`).
 */

/** Cap the snapshot so the LLM context stays tight. */
export const MAX_TRADES = 50;

/** UTF-8 size of a string (falls back to code units where TextEncoder is absent). */
function byteLength(s: string): number {
  return typeof TextEncoder === 'undefined'
    ? s.length
    : new TextEncoder().encode(s).length;
}

/**
 * Greedily keep trades (newest-first) until the next one would exceed the
 * byte budget for their serialized JSON, so a snapshot stays a contiguous
 * head of the tape. Unserializable or missing entries are skipped, never
 * thrown on.
 *
 * The budget exists because the whole outbound frame shares the
 * gateway-proxy's client read limit — `WS_MAX_CLIENT_MESSAGE_SIZE`, 32 KiB by
 * default (dime-terminal `api/terminal/gateway-proxy/config/config.go`). The
 * proxy hard-closes the connection (status 1009, no response frame) on any
 * frame over it, so an unbounded snapshot turns into a dropped connection.
 */
export function capTradesToByteBudget(
  trades: readonly unknown[],
  budgetBytes: number,
): unknown[] {
  const kept: unknown[] = [];
  // Charge the array's '[' and ']' up front; each kept trade then charges
  // its own bytes + 1 for a comma. That counts n commas for n trades where
  // the serialized array only has n−1 — one byte conservative, never over.
  let remaining = budgetBytes - 2;
  for (const trade of trades) {
    if (trade == null) continue;
    let json: string | undefined;
    try {
      json = JSON.stringify(trade);
    } catch {
      continue; // unserializable store item — skip it
    }
    if (json == null) continue;
    const cost = byteLength(json) + 1; // +1 for the joining comma
    if (cost > remaining) break;
    remaining -= cost;
    kept.push(trade);
  }
  return kept;
}
